import { useQuery } from '@tanstack/react-query'
import { tasksService } from '../lib/tasks/tasks-service'
import type { GTask } from '../lib/tasks/tasks-service'
import type { DeckWithStats } from '../types/domain'

export function useDashboardStats(decks: DeckWithStats[] | undefined) {
  // React Query: Tasks list — cached for 5 min so revisiting Home doesn't re-fetch
  const { data: pendingTasks = [], isLoading: calLoading } = useQuery({
    queryKey: ['tasks', 'pending'],
    queryFn: () => tasksService.listPendingTasks(50),
    staleTime: 5 * 60_000,   // 5 minutes: tasks don't change per-minute
    retry: false,             // Don't retry on 403 (missing scope) — fail fast
    throwOnError: false,      // Non-critical: never crash the dashboard
  })

  if (!decks) {
    return {
      pendingTasks,
      calLoading,
      overdueTasks: [],
      overdueDecks: [],
      pendingDecks: [],
      activeDecks: [],
      deckTaskMap: new Map<string, GTask>(),
      totalPending: 0
    }
  }

  const now = new Date()
  
  // ── "Pendientes ahora" = decks cuya task está vencida (due <= hoy) ──
  const overdueTasks = pendingTasks.filter((t: GTask) => {
    if (!t.due) return false
    return new Date(t.due) <= now
  })
  
  const overdueDecks: DeckWithStats[] = []
  for (const task of overdueTasks) {
    // Extract deckInstanceId from task notes
    const match = task.notes?.match(/deck_instance_id:([\w-]+)/)
    const deckId = match?.[1]
    if (!deckId) continue
    const matchedDeck = decks.find(d => d.instance.id === deckId)
    if (matchedDeck && !overdueDecks.includes(matchedDeck)) {
      overdueDecks.push(matchedDeck)
    }
  }

  // Si no hay tasks vencidas, caer en los decks con overdue FSRS
  const pendingDecks = overdueDecks.length > 0
    ? overdueDecks
    : decks.filter(d => (d.stats?.overdue_cards ?? 0) > 0)

  // ── Bloques centrales = todos los decks con due o new cards, con su hora de task ──
  const activeDecks = decks.filter(
    d => ((d.stats?.due_today ?? 0) + (d.stats?.overdue_cards ?? 0) + (d.stats?.new_cards ?? 0)) > 0
  )

  // Mapear deck → próxima task pendiente
  const deckTaskMap = new Map<string, GTask>()
  for (const task of pendingTasks) {
    const match = task.notes?.match(/deck_instance_id:([\w-]+)/)
    const deckId = match?.[1]
    if (!deckId) continue
    const matchedDeck = decks.find(d => d.instance.id === deckId)
    if (matchedDeck && !deckTaskMap.has(matchedDeck.instance.id)) {
      deckTaskMap.set(matchedDeck.instance.id, task)
    }
  }

  const totalPending = pendingDecks.reduce(
    (sum, d) => sum + (d.stats?.due_today ?? 0) + (d.stats?.overdue_cards ?? 0), 0
  )

  return {
    pendingTasks,
    calLoading,
    overdueTasks,
    overdueDecks,
    pendingDecks,
    activeDecks,
    deckTaskMap,
    totalPending
  }
}
